import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class Dashboard901MService {

  constructor(private http: HttpClient) { }

  /**
   * Get Code Combinations data (jak CodeComboFactory.setCombinationCodeByKey)
   * Action ID: 9
   * Zwraca dane z CHART_CODE (np. ES_FOR_24)
   */
  getCodeCombinations(
    baseUrl: string,
    combinationKey: number | string,
    filterParams: any = {}
  ): Observable<any> {
    let params = new HttpParams()
      .set('action_id', '9')
      .set('combination_key', String(combinationKey));

    // Dodaj dodatkowe parametry z filterParams jeśli są
    if (filterParams.scenario) {
      params = params.set('scenario', String(filterParams.scenario));
    }
    if (filterParams.jcd_key) {
      params = params.set('jcd_key', String(filterParams.jcd_key));
    }
    if (filterParams.tax_year) {
      params = params.set('tax_year', String(filterParams.tax_year));
    }

    return this.http.get<any>(
      baseUrl + '/loadJsonObject',
      { params, withCredentials: true }
    );
  }

  /**
   * Get all comments for 901(m) Calculation Reason
   * Action code: lb5ove
   */
  getAllComments(
    baseUrl: string,
    rowData: any,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const params = {
      action_code: 'lb5ove',
      tax_year: rowData.TAX_YEAR,
      scenario: filterParams.scenario || globalService?.globalParams?.scenario,
      jcd_key: filterParams.jcd_key || globalService?.globalParams?.jcd_key || 250,
      combination_key: rowData.LE_KEY,
      sso_id: globalService?.userSettingsData?.user?.sso_id
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key as keyof typeof params] !== undefined && params[key as keyof typeof params] !== null) {
        urlEncoded.set(key, String(params[key as keyof typeof params]));
      }
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlEncoded.toString(),
      { headers, withCredentials: true }
    );
  }

  /**
   * Get all comments for Adjustment Screen
   * Action code: 3k9awv
   */
  getAdjustmentComments(
    baseUrl: string,
    rowData: any,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const params = {
      action_code: '3k9awv',
      tax_year: rowData.TAX_YEAR,
      scenario: filterParams.scenario || globalService?.globalParams?.scenario,
      jcd_key: filterParams.jcd_key || globalService?.globalParams?.jcd_key || 250,
      combination_key: rowData.COMBINATION_KEY,
      sso_id: globalService?.userSettingsData?.user?.sso_id
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key as keyof typeof params] !== undefined && params[key as keyof typeof params] !== null) {
        urlEncoded.set(key, String(params[key as keyof typeof params]));
      }
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlEncoded.toString(),
      { headers, withCredentials: true }
    );
  }

  /**
   * Get adjustments data
   * Action code: giagg2
   */
  getAdjustments(
    baseUrl: string,
    combinationKey: any
  ): Observable<any> {
    const params = {
      action_code: 'giagg2',
      combination_key: combinationKey
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key as keyof typeof params] !== undefined && params[key as keyof typeof params] !== null) {
        urlEncoded.set(key, String(params[key as keyof typeof params]));
      }
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlEncoded.toString(),
      { headers, withCredentials: true }
    );
  }

  /**
   * Save 901(m) Calculation Reason form
   * Action code: k9hid6
   */
  saveCalculationReason(
    baseUrl: string,
    rowData: any,
    review: string,
    comments: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const json_arrOBJ = [{
      LE_KEY: rowData.LE_KEY,
      COMBINATION_KEY: rowData.COMBINATION_KEY,
      IS_901M_CALC_REQD: review,
      COMMENTS: comments
    }];

    const jsonSettings = [{
      tax_year: rowData.TAX_YEAR,
      TRANS_HEADER_KEY: rowData.TRANS_HEADER_KEY
    }];

    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.jsonObj = JSON.stringify(json_arrOBJ);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, String(params[key]));
      }
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_code=k9hid6',
      urlEncoded.toString(),
      { headers, withCredentials: true }
    );
  }

  /**
   * Save 901 Adjustment
   * Action code: 908psr
   */
  save901Adjustment(
    baseUrl: string,
    rowData: any,
    colData: any,
    comments: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const json_arrOBJ = [{
      combination_key: rowData.COMBINATION_KEY,
      adj_type: colData?.map,
      adj_amt: (colData?.map === 'POST_ADJ' ? 0 : rowData[colData?.map]),
      disallowed_ep_amt: rowData.DISQUALIFIED_TAX_ADJUSTMENT,
      posted_tax_adjustment: rowData.REMAINING_901_ADJ,
      signoff_y_n: rowData.SIGNOFF_Y_N,
      comments: comments
    }];

    const jsonSettings = [{
      group_trans_key: rowData.GROUP_TRANS_KEY
    }];

    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.jsonObj = JSON.stringify(json_arrOBJ);
    
    // Dodaj wymagane parametry z filterParams lub rowData
    params.tax_year = filterParams.tax_year || rowData.TAX_YEAR || params.tax_year;
    params.scenario = filterParams.scenario || rowData.SCENARIO || params.scenario;
    params.jcd_key = filterParams.jcd_key || rowData.JCD_KEY || params.jcd_key || 250;
    params.scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || params.scenarioCode || 'RAF';
    params.is_issue_key_enabled = params.is_issue_key_enabled || 'N';

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, String(params[key]));
      }
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_code=908psr',
      urlEncoded.toString(),
      { headers, withCredentials: true }
    );
  }

  save901AdjComment(
    baseUrl: string,
    combinationKey: any,
    field: any,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const json_arrOBJ = [{
      combination_key: combinationKey,
      comments: field?.COMMENTS || '',
      adj_type: field?.LE_TAX_TYPE || ''
    }];

    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.jsonObj = JSON.stringify(json_arrOBJ);
    params.tax_year = filterParams.tax_year || params.tax_year;
    params.scenario = filterParams.scenario || params.scenario;
    params.jcd_key = filterParams.jcd_key || params.jcd_key || 250;

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, String(params[key]));
      }
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_code=3k9awv',
      urlEncoded.toString(),
      { headers, withCredentials: true }
    );
  }
}
